import React, { useEffect, useState, useCallback } from "react";
import Filters from "../components/Filters";
import A5Matrix from "../components/A5Matrix";
import RekapCoverA5 from "../components/RekapCoverA5";
import { EmptyReport, ErrorState } from "../components/EmptyState";
import { api, monthLabel } from "../lib/api";
import { loadSignatures } from "../lib/signatures";
import { Printer, FileDown, ArrowLeft, Layers } from "lucide-react";
import { useNavigate } from "react-router-dom";

const CAKUPAN = [
  { key: "kelurahan", label: "Seluruh Kelurahan" },
  { key: "rw", label: "Per RW (Rekap)" },
  { key: "rt", label: "Detail per RT" },
  { key: "massal", label: "Cetak Massal per RW" },
];

export default function CetakA5() {
  const nav = useNavigate();
  const [filter, setFilter] = useState({ tahun: "2026", bulan: "september", rw: "", rt: "" });
  const [cakupan, setCakupan] = useState("kelurahan");
  const [docs, setDocs] = useState([]); // array of { data, rw, rt, scope, signature }
  const [cover, setCover] = useState(null); // { rekapRw, signature } for Cetak Massal
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(false); setDocs([]); setCover(null);
    const periode = `${monthLabel(filter.bulan)} ${filter.tahun}`;
    try {
      if (cakupan === "kelurahan") {
        const r = await api.a5Agregat(filter.tahun, filter.bulan, "kelurahan");
        if (r?.data) {
          const sig = await loadSignatures("kelurahan", null, null, filter.bulan, filter.tahun);
          setDocs([{ data: r.data, rw: "SELURUH RW", rt: "-", scope: "kelurahan", signature: sig, periode, bulan: filter.bulan, tahun: filter.tahun }]);
        }
      } else if (cakupan === "rw") {
        if (!filter.rw) return;
        const r = await api.a5Agregat(filter.tahun, filter.bulan, "rw", filter.rw);
        if (r?.data) {
          const sig = await loadSignatures("rw", filter.rw, null, filter.bulan, filter.tahun);
          setDocs([{ data: r.data, rw: filter.rw, rt: "SELURUH RT", scope: "rw", signature: sig, periode, bulan: filter.bulan, tahun: filter.tahun }]);
        }
      } else if (cakupan === "rt") {
        if (!filter.rw || !filter.rt) return;
        const r = await api.a5(filter.tahun, filter.bulan, filter.rw, filter.rt);
        if (r?.data) {
          const sig = await loadSignatures("rt", filter.rw, filter.rt, filter.bulan, filter.tahun);
          setDocs([{ data: r.data, rw: filter.rw, rt: filter.rt, scope: "rt", signature: sig, periode, bulan: filter.bulan, tahun: filter.tahun }]);
        }
      } else if (cakupan === "massal") {
        if (!filter.rw) return;
        // Cover: rekap 10 RW summary with the kelurahan signature
        const [rekap, coverSig, rtList] = await Promise.all([
          api.rekapRw(filter.tahun, filter.bulan),
          loadSignatures("rw", filter.rw, null, filter.bulan, filter.tahun),
          api.listRt(filter.rw),
        ]);
        setCover({ rekapRw: rekap?.data || [], signature: coverSig, periode, bulan: filter.bulan, tahun: filter.tahun });

        // Individual A.5 documents for each submitted RT in the RW
        const results = await Promise.all(
          (rtList.data || []).map((r) => api.a5(filter.tahun, filter.bulan, filter.rw, r.rt).then((res) => ({ rt: r.rt, res })))
        );
        const filled = results.filter((x) => x.res?.data);
        filled.sort((a, b) => a.rt.localeCompare(b.rt));
        const sigs = await Promise.all(filled.map((it) => loadSignatures("rt", filter.rw, it.rt, filter.bulan, filter.tahun)));
        const out = filled.map((it, i) => ({
          data: it.res.data, rw: filter.rw, rt: it.rt, scope: "rt", signature: sigs[i], periode, bulan: filter.bulan, tahun: filter.tahun,
        }));
        setDocs(out);
      }
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [filter.tahun, filter.bulan, filter.rw, filter.rt, cakupan]);

  useEffect(() => { load(); }, [load]);

  const canPreview = docs.length > 0 && !loading;
  const needsRw = cakupan === "rw" || cakupan === "rt" || cakupan === "massal";
  const needsRt = cakupan === "rt";

  return (
    <div className="space-y-5">
      <div className="no-print">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0e2b26] tracking-tight">Cetak Laporan Format F4 A.5</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Pilih cakupan wilayah dan periode yang ingin Anda cetak.</p>
      </div>

      <div className="no-print">
        <Filters value={filter} onChange={setFilter} onRefresh={load} showRw={needsRw} showRt={needsRt} />
      </div>

      <div className="no-print bg-white rounded-xl border border-[hsl(var(--border))] p-4 sm:p-5">
        <label className="block text-[0.7rem] font-bold tracking-wider text-[hsl(var(--muted-foreground))] uppercase mb-2">Cakupan Cetak</label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {CAKUPAN.map((o) => (
            <button
              key={o.key}
              onClick={() => setCakupan(o.key)}
              className={`h-11 rounded-lg text-sm font-semibold border transition ${cakupan === o.key ? "bg-[#0e2b26] text-white border-[#0e2b26]" : "bg-white text-[#0e2b26] border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"}`}
              data-testid={`cakupan-${o.key}`}
            >
              {o.key === "massal" && <Layers size={14} className="inline mr-1" />}
              {o.label}
            </button>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={() => window.print()} disabled={!canPreview} className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 h-11 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50" data-testid="print-btn">
            <Printer size={16} /> Cetak
          </button>
          <button onClick={() => window.print()} disabled={!canPreview} className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 h-11 rounded-lg bg-[#0e2b26] text-white text-sm font-semibold hover:bg-[#123832] disabled:opacity-50">
            <FileDown size={16} /> Simpan PDF
          </button>
          <button onClick={() => nav(-1)} className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 h-11 rounded-lg border border-[hsl(var(--border))] text-[#0e2b26] text-sm font-semibold hover:bg-[hsl(var(--muted))]">
            <ArrowLeft size={16} /> Kembali
          </button>
        </div>
        {cakupan === "massal" && (
          <div className="mt-3 text-xs text-[hsl(var(--muted-foreground))]">
            <Layers size={12} className="inline mr-1" />
            Hanya RT yang telah melapor yang dicetak. Setiap RT menjadi 1 halaman A.5 (F4 Landscape).
          </div>
        )}
      </div>

      {error && <ErrorState onRetry={load} />}

      {!error && loading && (
        <div className="bg-white rounded-xl border p-8"><div className="skeleton h-64 w-full"></div></div>
      )}

      {!error && !loading && (
        (cakupan === "rw" && !filter.rw) || (cakupan === "rt" && (!filter.rw || !filter.rt)) || (cakupan === "massal" && !filter.rw)
      ) && (
        <div className="bg-white border border-dashed rounded-xl p-8 text-center text-sm text-[hsl(var(--muted-foreground))]">
          Pilih {cakupan === "rt" ? "RW dan RT" : "RW"} terlebih dahulu.
        </div>
      )}

      {!error && !loading && docs.length === 0 && (
        (cakupan === "kelurahan" || (cakupan === "rw" && filter.rw) || (cakupan === "rt" && filter.rw && filter.rt) || (cakupan === "massal" && filter.rw))
      ) && <EmptyReport />}

      {docs.length > 0 && (
        <div className="a5-doc-stack space-y-5">
          {cakupan === "massal" && (
            <div className="no-print bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800">
              <strong>{docs.length}</strong> laporan A.5 siap dicetak untuk <strong>RW {filter.rw}</strong> · {monthLabel(filter.bulan)} {filter.tahun}
              {cover && <> · Halaman pertama: <strong>Rekap Kelurahan</strong>.</>}
            </div>
          )}
          {cakupan === "massal" && cover && (
            <RekapCoverA5
              periode={cover.periode}
              bulan={cover.bulan}
              tahun={cover.tahun}
              rw={filter.rw}
              rekapRw={cover.rekapRw}
              signature={cover.signature}
            />
          )}
          {docs.map((d, i) => (
            <A5Matrix
              key={`${d.rw}-${d.rt}-${i}`}
              data={d.data}
              periode={d.periode}
              rw={d.rw}
              rt={d.rt}
              scope={d.scope}
              signature={d.signature}
              bulan={d.bulan}
              tahun={d.tahun}
              showSignature={true}
            />
          ))}
        </div>
      )}
    </div>
  );
}
